import { EOnboardingKey } from '@constants';
import { IAcademy } from '@sdk/contracts';
import { Form, Select, Input } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionsButtons } from '../../ActionsButtons';
import { EmptyInfo } from '../../EmptyInfo';

import { Actions, Item, ItemTitle, ItemsWrapper, Wrapper } from './OnboardingContent.styles';

import { RULES } from '@/constants';
import { useMutateAcademies } from '@/store';

type OnboardingContentProps = {
  academy: IAcademy;
};

const OPTIONS = Object.values(EOnboardingKey).map(key => ({ label: key, value: key }));

export const OnboardingContent: React.FC<OnboardingContentProps> = ({ academy }) => {
  const { t } = useTranslation('AcademyDetailTranslations');
  const [form] = Form.useForm<IAcademy>();
  const [isEditing, setIsEditing] = useState(false);
  const { updateAcademy, isWorking } = useMutateAcademies();

  useEffect(() => {
    if (academy) form.setFieldsValue(academy);
  }, [academy]);

  const watchedKey = Form.useWatch(['onboarding', 'onboardingKey'], form);

  const initialValues = useMemo(
    () => ({ onboardingKey: academy.onboarding?.onboardingKey ?? '' }),
    [academy]
  );
  const currentValues = useMemo(() => ({ onboardingKey: watchedKey ?? '' }), [watchedKey]);

  const deepEqual = (a: unknown, b: unknown) => JSON.stringify(a ?? '') === JSON.stringify(b ?? '');
  const hasChanges = useMemo(
    () => isEditing && !deepEqual(currentValues, initialValues),
    [isEditing, currentValues, initialValues]
  );

  const handleToggleEdit = () => {
    if (isEditing) {
      form.setFieldsValue(academy);
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  const handleClose = () => setIsEditing(false);

  const handleSave = async () => {
    const values = await form.validateFields();
    const onboardingKey = values?.onboarding?.onboardingKey;
    updateAcademy(
      { academyId: academy._id, update: { onboarding: { onboardingKey } } },
      { onSuccess: () => handleClose() }
    );
  };

  if (!academy) return <EmptyInfo />;

  const displayOnboardingKey = watchedKey ?? academy.onboarding?.onboardingKey ?? '-';

  return (
    <Wrapper>
      <Actions>
        <ActionsButtons
          onEdit={handleToggleEdit}
          onSave={handleSave}
          hasChanges={hasChanges}
          isSaving={isWorking}
        />
      </Actions>

      <Form<IAcademy> form={form} layout="vertical">
        <ItemsWrapper>
          <Item>
            <ItemTitle>{t('ONBOARDING_SELECTOR')}</ItemTitle>
            {isEditing ? (
              <Form.Item
                name={['onboarding', 'onboardingKey']}
                rules={[RULES.REQUIRED]}
                style={{ maxWidth: 380, margin: 0 }}>
                <Select options={OPTIONS} placeholder={t('SELECT_ONBOARDING_PLACEHOLDER')} />
              </Form.Item>
            ) : (
              <Input disabled value={displayOnboardingKey} style={{ maxWidth: 380 }} />
            )}
          </Item>
        </ItemsWrapper>
      </Form>
    </Wrapper>
  );
};
